<template>
  <div class="handTypeComponent">
    <b-card no-body>
      <b-card-header class="d-flex align-items-center justify-content-between">
        <div class="mr-3">Hand Values</div>
        <b-dropdown variant="light" size="sm" no-caret>
          <template v-slot:button-content>
            <fa icon="bars" />
          </template>
          <b-dropdown-form>
            <b-form-checkbox v-model="multiSelect">Multiselect</b-form-checkbox>
          </b-dropdown-form>
        </b-dropdown>
      </b-card-header>

      <b-card-body v-if="isUpdating" class="loading">
        <fa icon="spinner-third" spin />
      </b-card-body>

      <b-card-body v-else>
        <b-list-group>
          <b-list-group-item
            v-for="item of items"
            :key="item.id"
            class="item"
            variant="info"
            button
            :active="toggledTypes[item.id]"
            @click="() => toggleType(item.id)"
            @mouseover="() => mouseover(item.id)"
            @mouseout="() => mouseout(item.id)"
          >
            <div class="label">{{ item.label }}</div>
            <div class="count">{{ item.count }} / {{ comboStrings.length }}</div>
          </b-list-group-item>
        </b-list-group>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import intersection from "lodash/intersection";
import mapValues from "lodash/mapValues";
import { getHandTypes } from "../api";
import { HANDTYPES } from "../constants";
import { allCombos, comboStringsToSquareStrings } from "../helpers/cards";

export default {
  name: "HandTypes",

  data() {
    return {
      isUpdating: false,
      toggledTypes: HANDTYPES.reduce((obj, handType) => {
        obj[handType] = false;
        return obj;
      }, {}),
      multiSelect: false,
      hoveredItem: null,
      // Basically a computed, but updated asynchronously from server data
      handTypeByComboString: {},
    };
  },

  props: {
    board: { type: Array, required: true },
    comboStrings: { type: Array, required: true },
    resetOnChange: { type: Number, default: 0 },
  },

  created() {
    this.updateHandTypes();
  },

  watch: {
    board() {
      this.updateHandTypes();
    },
    markedSquareStrings(value) {
      this.$emit("updateMarkedSquareStrings", value);
    },
    resetOnChange() {
      for (let key of Object.keys(this.toggledTypes)) {
        this.toggledTypes[key] = false;
      }
    },
  },

  computed: {
    comboStringsByHandType() {
      const result = {};
      for (let [comboString, handType] of Object.entries(this.handTypeByComboString)) {
        if (!result[handType]) result[handType] = [];
        result[handType].push(comboString);
      }
      return result;
    },
    filteredComboStringsByHandType() {
      return mapValues(this.comboStringsByHandType, comboStrings =>
        intersection(comboStrings, this.comboStrings)
      );
    },
    items() {
      // Hand types in descending order by value
      const handTypes = [...HANDTYPES].reverse();
      return handTypes
        .map(handType => {
          const comboStrings = this.filteredComboStringsByHandType[handType] || [];
          return {
            id: handType,
            label: handType === "high card" ? "Nothing" : handType,
            comboStrings,
            count: comboStrings.length,
          };
        })
        .filter(item => item.count > 0);
    },
    hoveredSquareStrings() {
      if (!this.hoveredItem) return [];
      const comboStrings = this.filteredComboStringsByHandType[this.hoveredItem];
      return comboStringsToSquareStrings(comboStrings);
    },
    toggledSquareStrings() {
      const toggledComboSets = Object.entries(this.toggledTypes)
        .filter(([id, value]) => value === true)
        .map(([id, value]) => this.filteredComboStringsByHandType[id]);
      const toggledCombos = [].concat(...toggledComboSets);
      return comboStringsToSquareStrings(toggledCombos);
    },
    markedSquareStrings() {
      return [...new Set([...this.hoveredSquareStrings, ...this.toggledSquareStrings])];
    },
  },

  methods: {
    updateHandTypes() {
      this.isUpdating = true;
      getHandTypes(this.board, allCombos).then(handTypeByComboString => {
        this.handTypeByComboString = handTypeByComboString;
        this.isUpdating = false;
      });
    },
    toggleType(id) {
      const value = !this.toggledTypes[id];
      this.toggledTypes[id] = value;
      if (value === true && !this.multiSelect) {
        for (let item of this.items) {
          if (item.id !== id) this.toggledTypes[item.id] = false;
        }
      }
    },
    mouseover(id) {
      this.hoveredItem = id;
    },
    mouseout(id) {
      this.hoveredItem = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.handTypeComponent {
  .loading {
    min-height: 150px;
  }
  .item {
    outline: none;
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    .label {
      text-transform: capitalize;
    }
  }
}
</style>
